import * as React from 'react';
import { isSSR } from 'oasis-os-utils';
/**
 * Check if the script is already in the DOM
 * Since this script is used to set the initial state, we want to double check
 * that the script is indeed loaded (and not just placed) because
 * loading a Feature App multiple times could prematurely set the status to ready.
 * If Server Side Rendering is running then it's safe to say that the script is already loaded
 * @param src URL to the script
 */
const isScriptInitiallyAvailable = (src) => !isSSR() &&
    !!document.querySelector(`script[src="${src}"][data-status="ready"]`);
const useDynamicFeature = (src) => {
    const canHydrate = isScriptInitiallyAvailable(src);
    const [status, setStatus] = React.useState(canHydrate ? 'ready' : 'idle');
    React.useEffect(() => {
        // Allow falsy src value if waiting on other data needed for
        // constructing the script URL passed to this hook.
        if (!src) {
            setStatus('idle');
            return;
        }
        // Fetch existing script element by src
        // It may have been added by another instance of this hook
        let script = document.querySelector(`script[src="${src}"]`);
        if (!script) {
            // Create script
            script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.setAttribute('data-status', 'loading');
            // Add script to document body
            document.body.appendChild(script);
            // Store status in attribute on script
            // This can be read by other instances of this hook
            const setAttributeFromEvent = (event) => {
                script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
            };
            script.addEventListener('load', setAttributeFromEvent);
            script.addEventListener('error', setAttributeFromEvent);
        }
        else {
            // Grab existing script status from attribute and set to state.
            setStatus(script.getAttribute('data-status'));
        }
        // Script event handler to update status in state
        // Note: Even if the script already exists we still need to add
        // event handlers to update the state for *this* hook instance.
        const setStateFromEvent = (event) => {
            setStatus(event.type === 'load' ? 'ready' : 'error');
        };
        // Add event listeners
        script.addEventListener('load', setStateFromEvent);
        script.addEventListener('error', setStateFromEvent);
        // Remove event listeners on cleanup
        return () => {
            script.removeEventListener('load', setStateFromEvent);
            script.removeEventListener('error', setStateFromEvent);
        };
    }, [src] // Only re-run effect if script src changes
    );
    return status;
};
export default useDynamicFeature;
